import React from 'react'
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    Legend
} from 'recharts'
import {Text} from '@/components/ui/text/Text'
import {useTranslation} from '@/translations/i18n'
import {
    StyledMainContainer,
    StyledChartContainer,
    StyledTooltipContainer,
    StyledTooltipItem,
    StyledTooltipDot
} from './style'
import {useTheme} from 'styled-components'
import {useGetDashboardOps} from '../../query/useGetDashboardOps'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {DashboardSearchParams} from '../../types'
import {chartTickFormatter} from '../../utils'
import {DashboardOperationalAccessesPerformanceChartSkeleton} from './DashboardOperationalAccessesPerformanceChartSkeleton'
import {formatNumber} from '@/utilities/helpers'

interface TooltipProps {
    active?: boolean
    payload?: Array<{
        color: string
        name: string
        value: number
        dataKey: string
        payload: {
            completed: number
            absences: number
        }
    }>
    label?: string
    type: 'day' | 'month'
}

const CustomTooltip: React.FC<TooltipProps> = ({active, payload, label, type}) => {
    if (active && payload && payload.length) {
        return (
            <StyledTooltipContainer>
                <Text palette="neutral" shade="900" size="textSm" weight={600}>
                    {chartTickFormatter(type, label ?? '')}
                </Text>
                {payload.map((entry, index) => (
                    <StyledTooltipItem key={index}>
                        <StyledTooltipDot $color={entry.color} />
                        <Text palette="neutral" shade="700" size="textSm" weight={400}>
                            {entry.name}:{' '}
                            {entry.dataKey == 'completedSum'
                                ? formatNumber(entry.payload.completed)
                                : formatNumber(entry.payload.absences)}{' '}
                            ({formatNumber(entry.value)})
                        </Text>
                    </StyledTooltipItem>
                ))}
            </StyledTooltipContainer>
        )
    }
    return null
}

export const DashboardOperationalAccessesPerformanceChart: React.FC = () => {
    const {palette} = useTheme()
    const {t} = useTranslation()
    const {searchParams} = useParsedSearchParams(DashboardSearchParams)
    const {data, isLoading} = useGetDashboardOps({fields: ['graph'], ...searchParams})

    if (isLoading) {
        return <DashboardOperationalAccessesPerformanceChartSkeleton />
    }

    return (
        <StyledMainContainer direction="column" gap={4} fullWidth>
            <StyledChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={data?.graph ?? []}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 20
                        }}
                    >
                        <Legend
                            iconType="circle"
                            iconSize={8}
                            verticalAlign="top"
                            align="right"
                            height={32}
                            formatter={value => {
                                return (
                                    <Text render="span" palette="neutral" shade="600" size="textSm" weight={400}>
                                        {value}
                                    </Text>
                                )
                            }}
                        />
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                            dataKey={data?.graph?.[0]?.month ? 'month' : 'day'}
                            tickFormatter={value =>
                                chartTickFormatter(data?.graph?.[0]?.month ? 'month' : 'day', value)
                            }
                            axisLine={false}
                            tickLine={false}
                            tick={{fontSize: 12, fill: palette.neutral[500]}}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{fontSize: 12, fill: palette.neutral[500]}}
                            tickFormatter={value => formatNumber(value)}
                            domain={data?.accessCounters?.planned ? [0, data?.accessCounters?.planned] : [0, 100]}
                        />
                        <Tooltip content={<CustomTooltip type={data?.graph?.[0]?.month ? 'month' : 'day'} />} />
                        {data?.accessCounters?.planned && (
                            <ReferenceLine
                                y={data?.accessCounters?.planned}
                                stroke={palette.neutral[600]}
                                strokeDasharray="5 5"
                                strokeWidth={2}
                                label={{
                                    value: `${t('dashboard:planned_accesses')}: ${formatNumber(data?.accessCounters?.planned ?? 0)}`,
                                    position: 'top',
                                    offset: 10,
                                    style: {
                                        fontSize: '12px',
                                        fill: palette.neutral[600],
                                        fontWeight: 500
                                    }
                                }}
                            />
                        )}
                        <Line
                            type="monotone"
                            dataKey="completedSum"
                            name={t('dashboard:performed')}
                            stroke={palette.primary[500]}
                            strokeWidth={2}
                            dot={{fill: palette.primary[500], strokeWidth: 2.5, r: 2}}
                            activeDot={{r: 5, stroke: palette.primary[500], strokeWidth: 2}}
                        />
                        <Line
                            type="monotone"
                            dataKey="absencesSum"
                            name={t('dashboard:absences')}
                            stroke={palette.orange[800]}
                            strokeWidth={2}
                            dot={{fill: palette.orange[800], strokeWidth: 2.5, r: 2}}
                            activeDot={{r: 5, stroke: palette.orange[800], strokeWidth: 2}}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </StyledChartContainer>
        </StyledMainContainer>
    )
}
